// src/context/AuthContext.js

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const AuthContext = createContext(null);

// Configure axios defaults
axios.defaults.baseURL = process.env.REACT_APP_API_URL || '';

// The exact three messages the backend's auth middleware (verifyToken and
// the role guards built on it) produce for a genuine auth-layer failure --
// no token sent, or a token that failed verification (including expiry).
// Deliberately NOT the generic "🙈 Oops! You don't have permission for
// that" message every permission-denied 403 in the app also uses -- that
// one means the person IS still logged in fine, they just lack access to
// that one action, and must never trigger a logout.
const SESSION_EXPIRED_MESSAGES = new Set(['Invalid or expired token', 'No token provided', 'Unauthorised']);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [permissions, setPermissions] = useState({});
  const [hasGreenfieldAccess, setHasGreenfieldAccess] = useState(false);
  const [token, setToken]     = useState(() => localStorage.getItem('dwpulse_token'));
  const [loading, setLoading] = useState(true);

  // Whatever action was being taken anywhere in the app when a session
  // genuinely expires, this catches it in one place instead of leaving
  // every page to show the server's raw "Invalid or expired token" text
  // with no clear next step -- clears the stale session (the same way
  // logout() already does, which is what naturally brings the login
  // screen back up) and explains in plain language what happened.
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const message = error?.response?.data?.error;
        const isSessionExpired = (status === 401 || status === 403) && SESSION_EXPIRED_MESSAGES.has(message);
        if (isSessionExpired && localStorage.getItem('dwpulse_token')) {
          localStorage.removeItem('dwpulse_token');
          setToken(null);
          setUser(null);
          delete axios.defaults.headers.common['Authorization'];
          toast.error('Your session has expired — please log in again.');
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  // Attach token to all axios requests
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // On mount — verify token and load user profile
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get('/api/auth/me');
        setUser(res.data);
        try {
          const permsRes = await axios.get('/api/permissions/mine');
          setPermissions(permsRes.data);
        } catch { /* non-fatal — nav/UI just falls back to role-based defaults */ }
        // Greenfield access is per-user, not part of the role_permissions
        // system above -- a 403 here just means "this account doesn't
        // have it," which is the normal case for almost everyone, not an
        // error. Only super_admin bypasses this check server-side, so
        // most accounts will see a 403 every time, harmlessly.
        try {
          await axios.get('/api/greenfield/access-check');
          setHasGreenfieldAccess(true);
        } catch { setHasGreenfieldAccess(false); }
      } catch {
        // Token invalid/expired
        localStorage.removeItem('dwpulse_token');
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    verifyToken();
  }, [token]);

  const login = useCallback(async (username, password) => {
    const res = await axios.post('/api/auth/login', { username, password });
    const { token: newToken, user: userData } = res.data;

    localStorage.setItem('dwpulse_token', newToken);
    setToken(newToken);
    setUser(userData);
    try {
      const permsRes = await axios.get('/api/permissions/mine', { headers: { Authorization: `Bearer ${newToken}` } });
      setPermissions(permsRes.data);
    } catch { /* non-fatal */ }
    try {
      await axios.get('/api/greenfield/access-check', { headers: { Authorization: `Bearer ${newToken}` } });
      setHasGreenfieldAccess(true);
    } catch { setHasGreenfieldAccess(false); }
    return userData;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post('/api/auth/logout');
    } catch {}
    localStorage.removeItem('dwpulse_token');
    setToken(null);
    setUser(null);
    setHasGreenfieldAccess(false);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    await axios.post('/api/auth/change-password', { currentPassword, newPassword });
  }, []);

  // Role helpers — 'senior_manager' accepted as a legacy alias for 'admin'
  // so an already-issued session doesn't lose access mid-session.
  const isSuperAdmin     = user?.role === 'super_admin';
  const isSeniorManager  = isSuperAdmin || user?.role === 'admin' || user?.role === 'senior_manager';
  const isTeamLead       = user?.role === 'team_lead';
  const isSupervisor     = user?.role === 'supervisor';
  const isAssistantSup   = user?.role === 'assistant_supervisor';
  const isCSR            = user?.role === 'csr' || user?.role === 'employee';
  const isTeamLeadPlus   = isSeniorManager || isTeamLead;
  const isSupervisorPlus = isSeniorManager || isTeamLead || isSupervisor || isAssistantSup;

  // can(key) — reads a raw permission value ('true'/'false' or an enum
  // string like 'own_store'/'company_wide'/'hidden') from what the server
  // resolved for this user's role. Falls back to 'false' if not yet
  // loaded, so UI defaults to hidden/off rather than briefly flashing
  // something a permission-off role shouldn't see.
  const can = useCallback((key) => permissions[key] ?? 'false', [permissions]);

  return (
    <AuthContext.Provider value={{
      user, token, loading,
      login, logout, changePassword,
      isSuperAdmin, isSeniorManager, isTeamLead, isSupervisor, isAssistantSup, isCSR,
      isTeamLeadPlus, isSupervisorPlus,
      permissions, can, hasGreenfieldAccess,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
